import React, { useState, useMemo } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { toast } from 'sonner'
import { Search, ChevronDown, X } from 'lucide-react'

// Custom hook for debounced search
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

// Helper function to detect "me" search
function isMeSearch(searchTerm: string): boolean {
  return searchTerm.toLowerCase().trim().includes('me')
}

// Helper function to prioritize current user in filtered results
function prioritizeCurrentUserInResults<T extends BaseNode>(
  filtered: T[],
  currentUser: T | null | undefined,
  isCurrentUserSelected: boolean
): T[] {
  if (
    !currentUser ||
    !currentUser.id ||
    !currentUser.name ||
    !currentUser.name.trim() ||
    isCurrentUserSelected
  ) {
    return filtered
  }

  const currentUserInResults = filtered.find((node) => node.id === currentUser.id)
  if (currentUserInResults) {
    // Remove current user from their current position and put them first
    return [currentUser, ...filtered.filter((node) => node.id !== currentUser.id)]
  } else {
    // If current user wasn't in filtered results when searching for "me", add them at the beginning
    // This allows users to find themselves by typing "me" even if their name doesn't contain "me"
    return [currentUser, ...filtered]
  }
}

// Helper function to ensure current user availability
function ensureCurrentUserAvailability<T extends BaseNode>(
  nodes: T[],
  currentUser: T | null | undefined,
  isCurrentUserSelected: boolean
): T[] {
  if (
    !currentUser ||
    !currentUser.id ||
    !currentUser.name ||
    !currentUser.name.trim() ||
    isCurrentUserSelected
  ) {
    return nodes
  }

  const currentUserInNodes = nodes.find((node) => node.id === currentUser.id)
  if (!currentUserInNodes) {
    return [...nodes, currentUser]
  }

  return nodes
}

export interface BaseNode {
  id: string
  name: string
}

export interface NodeRelationshipSelectionProps<T extends BaseNode> {
  // Common props
  availableNodes: T[]
  loading?: boolean
  disabled?: boolean
  label?: string | React.ReactNode
  placeholder?: string
  className?: string
  error?: string

  // Mode selection
  mode: 'single' | 'multi'

  // Single selection props
  selectedNode?: T | null
  onNodeChange?: (node: T | null) => void

  // Multi selection props
  selectedNodes?: T[]
  onAddNode?: (node: T) => void
  onRemoveNode?: (nodeId: string) => void
  maxSelections?: number

  // Node-specific rendering
  renderNode?: (node: T, size?: 'xs' | 'sm' | 'md') => React.ReactNode
  getNodeDescription?: (node: T) => string

  // Custom dropdown item rendering (for complex layouts like tasks)
  renderDropdownItem?: (node: T) => React.ReactNode

  // Custom input value formatting (for cases like "name (email)" display)
  getInputValue?: (node: T, searchTerm: string) => string

  // Multi-selection specific props
  directMessage?: boolean

  // Current user for "me" search prioritization
  currentUser?: T | null

  // Accessibility props
  id?: string
}

export function NodeRelationshipSelection<T extends BaseNode>({
  availableNodes,
  loading = false,
  disabled = false,
  label = 'Select',
  placeholder = 'Search...',
  className = '',
  error,
  mode,
  selectedNode,
  onNodeChange,
  selectedNodes = [],
  onAddNode,
  onRemoveNode,
  maxSelections,
  renderNode,
  getNodeDescription = (_node: T) => '',
  renderDropdownItem,
  getInputValue,
  directMessage = false,
  currentUser,
  id,
}: NodeRelationshipSelectionProps<T>) {
  const [searchTerm, setSearchTerm] = useState('')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState(-1)

  // Generate unique ID for accessibility
  const elementId = React.useId()
  const uniqueId = id || `node-selection-${elementId}`

  // Debounce search term for better performance
  const debouncedSearchTerm = useDebounce(searchTerm, 300)

  // Get nodes available to select based on mode
  const availableToSelectNodes: T[] = useMemo(() => {
    if (mode === 'single') {
      let filtered = availableNodes
      if (selectedNode) {
        filtered = availableNodes.filter((node) => node.id !== selectedNode.id)
      }

      // Ensure current user remains available for "me" search
      filtered = ensureCurrentUserAvailability(
        filtered,
        currentUser,
        selectedNode?.id === currentUser?.id
      )

      return filtered
    } else {
      // Multi-selection: exclude already selected nodes
      let filtered = availableNodes.filter(
        (node) => !selectedNodes.find((selectedNode) => selectedNode.id === node.id)
      )

      // Ensure current user remains available for "me" search
      filtered = ensureCurrentUserAvailability(
        filtered,
        currentUser,
        selectedNodes.find((selectedNode) => selectedNode.id === currentUser?.id) !== undefined
      )

      return filtered
    }
  }, [availableNodes, selectedNode, selectedNodes, mode, currentUser])

  // Memoized filtered nodes for better performance
  const filteredNodes = useMemo(() => {
    if (!availableToSelectNodes.length) return []

    if (!debouncedSearchTerm.trim()) {
      // Show first 20 nodes when no search term (better UX than showing all)
      return availableToSelectNodes.slice(0, 20)
    }

    let filtered = availableToSelectNodes.filter(
      (node) =>
        node.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        getNodeDescription(node).toLowerCase().includes(debouncedSearchTerm.toLowerCase())
    )

    // If searching for "me" and we have a valid current user, prioritize them
    if (isMeSearch(debouncedSearchTerm)) {
      const isCurrentUserSelected =
        mode === 'single'
          ? selectedNode?.id === currentUser?.id
          : selectedNodes.find((selectedNode) => selectedNode.id === currentUser?.id) !== undefined

      filtered = prioritizeCurrentUserInResults(filtered, currentUser, isCurrentUserSelected)
    }

    return filtered
  }, [
    availableToSelectNodes,
    debouncedSearchTerm,
    getNodeDescription,
    currentUser,
    mode,
    selectedNode,
    selectedNodes,
  ])

  // Auto-select first item when dropdown opens and there are nodes available
  React.useEffect(() => {
    if (filteredNodes.length > 0 && isDropdownOpen && focusedIndex === -1) {
      setFocusedIndex(0)
    }
  }, [filteredNodes.length, isDropdownOpen, focusedIndex])

  // Reset focused index when dropdown closes or no results available
  React.useEffect(() => {
    if (!isDropdownOpen || filteredNodes.length === 0) {
      setFocusedIndex(-1)
    }
  }, [isDropdownOpen, filteredNodes.length])

  // Check if we've reached selection limits (for multi-selection)
  const hasReachedLimit =
    mode === 'multi' &&
    ((maxSelections && selectedNodes.length >= maxSelections) ||
      (directMessage && selectedNodes.length >= 1))

  // Handle node selection based on mode
  const handleSelectNode = (newNode: T) => {
    if (mode === 'single') {
      onNodeChange?.(newNode)
      setSearchTerm('')
      setIsDropdownOpen(false)
      setFocusedIndex(-1)
    } else {
      const isAlreadySelected = selectedNodes.find((node) => node.id === newNode.id)

      if (isAlreadySelected) {
        toast.info(`${newNode.name} is already selected`)
        return
      }

      if (hasReachedLimit) {
        const max = maxSelections || 1
        toast.info(`Maximum ${max} ${max === 1 ? 'node' : 'nodes'} allowed`)
        return
      }

      onAddNode?.(newNode)
      setSearchTerm('')
      setIsDropdownOpen(false)
      setFocusedIndex(-1)
    }
  }

  // Handle node removal (multi-selection only)
  const handleRemoveNode = (nodeId: string) => {
    if (mode === 'multi') {
      onRemoveNode?.(nodeId)
    }
  }

  // Handle clearing selection (single selection only)
  const handleClearSelection = () => {
    if (mode === 'single') {
      onNodeChange?.(null)
      setSearchTerm('')
    }
  }

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm(value)

    // If user is typing and we have a selection, clear it to allow new search (single mode)
    if (mode === 'single' && selectedNode) {
      const expectedValue = getInputValue ? getInputValue(selectedNode, '') : selectedNode.name
      if (value !== expectedValue) {
        onNodeChange?.(null)
      }
    }

    setIsDropdownOpen(true)
  }

  // Handle input focus
  const handleInputFocus = () => {
    setIsDropdownOpen(true)
    // Don't reset focusedIndex here - let the useEffect handle auto-selection
  }

  // Handle input blur
  const handleInputBlur = () => {
    // Delay closing to allow for click events on dropdown items
    setTimeout(() => {
      setIsDropdownOpen(false)
      // focusedIndex will be reset by the useEffect when isDropdownOpen becomes false
    }, 200)
  }

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isDropdownOpen || filteredNodes.length === 0) return

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault()
        setFocusedIndex((prev) => (prev < filteredNodes.length - 1 ? prev + 1 : 0))
        break
      }

      case 'ArrowUp': {
        e.preventDefault()
        setFocusedIndex((prev) => (prev > 0 ? prev - 1 : filteredNodes.length - 1))
        break
      }

      case 'Enter':
        e.preventDefault()
        if (focusedIndex >= 0 && focusedIndex < filteredNodes.length) {
          handleSelectNode(filteredNodes[focusedIndex])
        }
        break

      case 'Escape': {
        e.preventDefault()
        setIsDropdownOpen(false)
        setFocusedIndex(-1)
        // Return focus to input
        document.getElementById(uniqueId)?.focus()
        break
      }

      case 'Tab': {
        setIsDropdownOpen(false)
        setFocusedIndex(-1)
        break
      }
    }
  }

  // Format display value for the input
  const getDisplayValue = () => {
    if (mode === 'single') {
      if (selectedNode) {
        if (getInputValue) {
          return searchTerm || getInputValue(selectedNode, searchTerm)
        }
        return searchTerm || selectedNode.name
      }
      return searchTerm
    } else {
      return searchTerm
    }
  }

  // Get selection count text
  const getSelectionCountText = () => {
    if (mode === 'single') {
      return selectedNode ? '1 node selected' : ''
    } else {
      const effectiveMax = maxSelections || (directMessage ? 1 : '∞')
      return `${selectedNodes.length} of ${effectiveMax} selected`
    }
  }

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex items-center justify-between">
        <Label htmlFor={uniqueId}>
          {label}
          {mode === 'multi' && directMessage && (
            <span className="text-muted-foreground text-xs ml-1">
              (Direct Message - 1 node max)
            </span>
          )}
          {mode === 'multi' && maxSelections && !directMessage && (
            <span className="text-muted-foreground text-xs ml-1">
              (Maximum {maxSelections} {maxSelections === 1 ? 'node' : 'nodes'})
            </span>
          )}
        </Label>
        {(mode === 'single' ? selectedNode : selectedNodes.length > 0) && (
          <span className="text-xs text-muted-foreground">{getSelectionCountText()}</span>
        )}
      </div>

      {/* Selected Nodes Display (Multi-selection) */}
      {mode === 'multi' && selectedNodes.length > 0 && (
        <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-muted/30">
          {selectedNodes.map((selectedNode) => (
            <Badge key={selectedNode.id} variant="secondary" className="gap-2 pr-1 h-auto">
              {renderNode?.(selectedNode, 'sm')}
              <span className="font-medium">{selectedNode.name}</span>
              <button
                type="button"
                onClick={() => handleRemoveNode(selectedNode.id)}
                className="ml-1 hover:text-destructive transition-colors"
                aria-label={`Remove ${selectedNode.name} from ${label}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Node Selection Input */}
      <div className="space-y-2">
        <div className="relative">
          <div className="relative">
            {/* Search icon - only show when no node is selected */}
            {!selectedNode && (
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            )}

            {/* Selected node indicator - show when node is selected (single mode) */}
            {mode === 'single' && selectedNode && (
              <div className="absolute left-3 top-1/2 -translate-y-1/2">
                {renderNode ? (
                  renderNode(selectedNode, 'sm')
                ) : (
                  <div className="w-4 h-4 rounded-full bg-primary border-2 border-background" />
                )}
              </div>
            )}

            <Input
              placeholder={
                hasReachedLimit && mode === 'multi'
                  ? 'Limit reached'
                  : availableNodes.length > 0
                    ? placeholder
                    : 'Loading nodes...'
              }
              value={getDisplayValue()}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              onKeyDown={handleKeyDown}
              disabled={(hasReachedLimit && mode === 'multi') || loading || disabled}
              className={mode === 'single' && selectedNode ? 'pl-12 pr-10' : 'pl-10 pr-10'}
              aria-expanded={isDropdownOpen}
              aria-haspopup="listbox"
              aria-activedescendant={focusedIndex >= 0 ? `option-${focusedIndex}` : undefined}
              role="combobox"
              id={uniqueId}
            />

            {/* Clear button or dropdown toggle */}
            {availableToSelectNodes.length > 0 && !disabled && (
              <button
                type="button"
                onClick={() => {
                  if (mode === 'single' && selectedNode) {
                    handleClearSelection()
                  } else {
                    setIsDropdownOpen(!isDropdownOpen)
                  }
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-accent rounded-sm transition-colors"
                aria-label={
                  mode === 'single' && selectedNode
                    ? `Clear selected node (${selectedNode.name})`
                    : isDropdownOpen
                      ? 'Close node list'
                      : 'Open node list'
                }
              >
                {mode === 'single' && selectedNode ? (
                  <X className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronDown
                    className={`h-4 w-4 text-muted-foreground transition-transform ${
                      isDropdownOpen ? 'rotate-180' : ''
                    }`}
                  />
                )}
              </button>
            )}
          </div>

          {/* Nodes Dropdown */}
          {isDropdownOpen &&
            availableToSelectNodes.length > 0 &&
            !(hasReachedLimit && mode === 'multi') &&
            !disabled && (
              <div
                className="absolute z-50 w-full mt-1 border rounded-md bg-popover shadow-md max-h-64 overflow-y-auto"
                role="listbox"
                aria-label={`${label} options`}
              >
                {filteredNodes.length > 0 ? (
                  <>
                    {filteredNodes.map((availableNode, index) => {
                      const isFocused = focusedIndex === index
                      return (
                        <button
                          key={availableNode.id}
                          type="button"
                          id={isFocused ? `option-${index}` : undefined}
                          onClick={() => handleSelectNode(availableNode)}
                          onMouseEnter={() => setFocusedIndex(index)}
                          className={`w-full text-left transition-colors first:rounded-t-md last:rounded-b-md ${
                            renderDropdownItem
                              ? `p-3 ${isFocused ? 'bg-accent' : 'hover:bg-accent'}`
                              : `p-3 ${isFocused ? 'bg-accent' : 'hover:bg-accent'} flex items-center gap-3`
                          }`}
                          aria-selected={isFocused}
                          role="option"
                        >
                          {renderDropdownItem ? (
                            renderDropdownItem(availableNode)
                          ) : (
                            <>
                              {renderNode?.(availableNode, 'md')}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{availableNode.name}</p>
                                {getNodeDescription && getNodeDescription(availableNode) && (
                                  <p className="text-xs text-muted-foreground truncate">
                                    {getNodeDescription(availableNode)}
                                  </p>
                                )}
                              </div>
                            </>
                          )}
                        </button>
                      )
                    })}
                    {availableToSelectNodes.length > filteredNodes.length && (
                      <div className="p-2 text-xs text-muted-foreground text-center border-t bg-muted/50">
                        {debouncedSearchTerm.trim()
                          ? `Showing ${filteredNodes.length} of ${availableToSelectNodes.length} matching nodes`
                          : `Showing first ${filteredNodes.length} of ${availableToSelectNodes.length} available nodes. Type to search.`}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="p-4 text-center text-muted-foreground">
                    {debouncedSearchTerm.trim()
                      ? `No nodes found matching "${debouncedSearchTerm}"`
                      : 'No nodes available'}
                  </div>
                )}
              </div>
            )}
        </div>

        {/* Helper text */}
        {!disabled &&
          availableToSelectNodes.length > 0 &&
          !(hasReachedLimit && mode === 'multi') && (
            <p className="text-xs text-muted-foreground">
              {mode === 'single'
                ? selectedNode
                  ? `Selected: ${selectedNode.name}. Click the X to select a different node.`
                  : `Add an node by searching or browsing the list above. ${availableToSelectNodes.length} nodes available to select.`
                : directMessage || maxSelections === 1
                  ? `Select ${maxSelections || 1} ${maxSelections === 1 ? 'node' : 'nodes'} for this ${directMessage ? 'direct message' : 'selection'}`
                  : `Add nodes by searching or browsing the list above. ${availableToSelectNodes.length} nodes available to add.`}
            </p>
          )}

        {mode === 'multi' && hasReachedLimit && (
          <p className="text-xs text-amber-600 flex items-center gap-1">
            {maxSelections
              ? `Maximum ${maxSelections} ${maxSelections === 1 ? 'node' : 'nodes'} reached. Remove ${maxSelections === 1 ? 'the node' : 'an node'} to select a different one.`
              : 'Limit reached. Remove the current node to select a different one.'}
          </p>
        )}

        {error && <p className="text-sm text-destructive">{error}</p>}
      </div>
    </div>
  )
}
